import { isCapacitorIOS, isCapacitorNative } from "@/lib/capacitor"
import {
  getKeyboardVisibleBottom,
  getKeyboardVisibleTop,
  KEYBOARD_FIELD_GAP_PX,
  syncAllKeyboardLayouts,
} from "@/lib/capacitor/keyboard-chrome"

let scrollDebounceId = 0
let scrollRetryId = 0

function isScrollableOverflow(overflowY: string) {
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay"
}

function findScrollContainerInDialog(element: HTMLElement, dialog: HTMLElement): HTMLElement {
  let container = dialog
  let parent = element.parentElement

  while (parent && parent !== dialog) {
    if (isScrollableOverflow(window.getComputedStyle(parent).overflowY)) {
      container = parent
    }
    parent = parent.parentElement
  }

  return container
}

function findPageScrollContainer(element: HTMLElement): HTMLElement | null {
  let container: HTMLElement | null = null
  let parent = element.parentElement

  while (parent && parent !== document.documentElement) {
    if (parent.matches("main.cap-mobile-main-pad")) {
      return parent
    }
    if (isScrollableOverflow(window.getComputedStyle(parent).overflowY)) {
      container = parent
    }
    parent = parent.parentElement
  }

  return container
}

function shouldSkipPageKeyboardScroll(element: HTMLElement): boolean {
  if (element.closest(".chat-mobile-fixed")) return true
  if (element.closest("[data-skip-keyboard-scroll]")) return true
  if (element.closest('[role="dialog"]')) return true
  if (document.documentElement.hasAttribute("data-modal-overlay-open")) return true
  return false
}

function scrollContainerByDelta(container: HTMLElement, delta: number) {
  if (Math.abs(delta) < 1) return
  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight)
  container.scrollTop = Math.min(Math.max(0, container.scrollTop + delta), maxScroll)
}

function isFieldFullyVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()
  const viewBottom = getKeyboardVisibleBottom()
  const viewTop = getKeyboardVisibleTop()
  return rect.top >= viewTop && rect.bottom <= viewBottom + 2
}

function alignFieldAboveKeyboard(element: HTMLElement, mode: "page" | "modal"): void {
  syncAllKeyboardLayouts()

  const dialog = mode === "modal" ? (element.closest('[role="dialog"]') as HTMLElement | null) : null
  const container =
    dialog != null
      ? findScrollContainerInDialog(element, dialog)
      : shouldSkipPageKeyboardScroll(element)
        ? null
        : findPageScrollContainer(element)

  const viewBottom = getKeyboardVisibleBottom()
  const viewTop = getKeyboardVisibleTop()
  const rect = element.getBoundingClientRect()

  if (container) {
    if (rect.bottom > viewBottom) {
      scrollContainerByDelta(container, rect.bottom - viewBottom)
    }

    const afterDown = element.getBoundingClientRect()
    if (afterDown.top < viewTop) {
      scrollContainerByDelta(container, afterDown.top - viewTop)
    }

    if (isFieldFullyVisible(element)) return
  }

  element.scrollIntoView({ block: "nearest", inline: "nearest" })

  const afterNative = element.getBoundingClientRect()
  if (afterNative.bottom > viewBottom && container) {
    scrollContainerByDelta(container, afterNative.bottom - viewBottom)
  }
}

function runFieldScrollPasses(element: HTMLElement, mode: "page" | "modal") {
  alignFieldAboveKeyboard(element, mode)

  requestAnimationFrame(() => {
    alignFieldAboveKeyboard(element, mode)
    requestAnimationFrame(() => alignFieldAboveKeyboard(element, mode))
  })
}

export function cancelCapacitorKeyboardScroll() {
  if (scrollDebounceId) {
    window.clearTimeout(scrollDebounceId)
    scrollDebounceId = 0
  }
  if (scrollRetryId) {
    window.clearTimeout(scrollRetryId)
    scrollRetryId = 0
  }
}

function scheduleFieldScroll(element: HTMLElement, mode: "page" | "modal") {
  cancelCapacitorKeyboardScroll()

  syncAllKeyboardLayouts()

  const delays = isCapacitorIOS() ? [0, 50, 120, 220, 360, 520] : [0, 40, 120, 220]

  for (const delay of delays) {
    window.setTimeout(() => {
      runFieldScrollPasses(element, mode)
    }, delay)
  }

  scrollDebounceId = window.setTimeout(() => {
    scrollDebounceId = 0
    runFieldScrollPasses(element, mode)
  }, delays[delays.length - 1] + 40)
}

export function scheduleKeyboardScrollForFocusedField(element: HTMLElement | null | undefined) {
  if (!element || !isCapacitorNative()) return

  if (element.closest('[role="dialog"]')) {
    scheduleFieldScroll(element, "modal")
    return
  }

  if (shouldSkipPageKeyboardScroll(element)) return
  scheduleFieldScroll(element, "page")
}

export function shouldAdjustCapacitorKeyboardForFocusedInput(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el instanceof HTMLTextAreaElement) return true
  if (el instanceof HTMLSelectElement) return true
  if (el instanceof HTMLInputElement) {
    const t = el.type
    if (t === "checkbox" || t === "radio" || t === "hidden" || t === "file" || t === "button" || t === "submit") {
      return false
    }
    return true
  }
  if (el.isContentEditable) return true
  return false
}

export { KEYBOARD_FIELD_GAP_PX }
